import { ProcessDocumentDesigner } from './ProcessDocumentDesigner';
import { loadProcessDocumentDesign, saveDesignedProcessDocument } from '@/lib/processDocumentDesignStorage';
import { RotatableDocumentPreview } from './RotatableDocumentPreview';
import { documentPreviewKind } from '@/lib/documentRotation';
import { CostSheetWorkflow } from './CostSheetWorkflow';
import { CostSheetColorPresetDialog } from "./CostSheetColorPresetDialog";
import React from "react";
import { liveCostSheet } from "@/lib/costSheetLivePricing";
import { CostSheetSourcePicker } from "./CostSheetSourcePicker";
import { PdfPreview } from "./PdfPreview";
import { BookOpen, Copy, Edit, ExternalLink, FileImage, Lock, Plus, Printer, Save, Trash2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { ManufacturingProductSelector } from "@/components/manufacturing/ManufacturingProductSelector";
import { CostSheetMaterialLineDialog } from "@/components/manufacturing/CostSheetMaterialLineDialog";
import { CostSheetProcessMaterialsView } from "@/components/manufacturing/CostSheetProcessMaterialsView";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import {
  isCostSheetValidated,
  MFG_COST_SHEET_STAGES,
  MFG_COST_SHEET_VALIDATION_PASSWORD,
  nextStageValue,
  stageStateMeta,
} from "@/lib/constants";
import { formatFamilyTemplateOption } from "@/lib/manufacturingDuplicate";
import { printCostSheetCompact } from "@/lib/costSheetPrint";
import {
  applyTemplateToCostSheet,
  calculateSheetTotals,
  deleteMaterialLine,
  reorderCostSheetMaterialLines,
  reorderCostSheetProcessLines,
  deleteProcessDocument,
  deleteProcessLine,
  fetchCostSheets,
  fetchManufacturingProduct,
  fetchMaterials,
  fetchOrCreateCostSheet,
  fetchProcessDocuments,
  fetchProcesses,
  fetchSuppliers,
  fetchTemplates,
  formatMoney,
  LEGACY_SALE_PRICE_FIELDS,
  saveCostSheet,
  saveMaterialLine,
  saveProcessDocument,
  saveProcessLine,
} from "@/lib/api/manufacturing";

const BUCKET = "mfg-product-docs";
const initialProcessLine = {
  process_id: "",
  supplier_id: "",
  quantity: 1,
  unit_cost: 0,
  sort_order: 0,
  notes: "",
};

const initialDocument = {
  document_requirement_id: "",
  document_type: "image",
  title: "",
  storage_path: "",
  external_url: "",
  file_name: "",
  mime_type: "",
  file_size_bytes: "",
  notes: "",
  is_default: false,
};

const salePriceLabels = ["Tarifa 1", "Tarifa 2", "Tarifa 3", "Tarifa 4", "Tarifa 5", "Tarifa 6"];
const imageExtensionPattern = /\.(avif|bmp|gif|heic|ico|jpe?g|png|svg|tiff?|webp)(\?.*)?$/i;

function isImageDocument(doc) {
  return (
    doc?.document_type === "image" ||
    doc?.mime_type?.startsWith("image/") ||
    imageExtensionPattern.test(doc?.file_name || "") ||
    imageExtensionPattern.test(doc?.storage_path || "") ||
    imageExtensionPattern.test(doc?.external_url || "")
  );
}

function CostSummary({ sheet }) {
  const totals = sheet?.totals || calculateSheetTotals(sheet || {});

  return (
    <div className="grid gap-3 md:grid-cols-3">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="text-sm text-gray-600">Procesos</div>
        <div className="text-2xl font-bold">{formatMoney(totals.processOwnTotal)}</div>
      </div>
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="text-sm text-gray-600">Materiales</div>
        <div className="text-2xl font-bold">{formatMoney(totals.materialTotal)}</div>
      </div>
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="text-sm text-gray-600">Total escandallo</div>
        <div className="text-2xl font-bold text-primary">{formatMoney(totals.total)}</div>
      </div>
    </div>
  );
}

function ProcessDocuments({ processLine, product, onChanged, readOnly = false }) {
  const requirements = processLine.process?.document_requirements || [];
  const [designSession, setDesignSession] = React.useState(null);
  const designContext = React.useMemo(() => ({ article: product?.code || '', process: processLine.process?.description || '' }), [product?.code, processLine.process?.description]);
  const [documents, setDocuments] = React.useState(processLine.documents || []);
  const [documentEditorOpen, setDocumentEditorOpen] = React.useState(false);
  const [documentToDelete, setDocumentToDelete] = React.useState(null);
  const deletingDocument = React.useRef(false);
  const photoInputRef = React.useRef(null);
  const [formData, setFormData] = React.useState(initialDocument);
  const [editingId, setEditingId] = React.useState(null);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [rotationBusy, setRotationBusy] = React.useState(false);
  const [uploadEnabled, setUploadEnabled] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [imageUrls, setImageUrls] = React.useState({});
  const [previewDoc, setPreviewDoc] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState("");
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const { toast } = useToast();

  const loadDocuments = React.useCallback(async () => {
    const result = await fetchProcessDocuments(processLine.id);
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar los documentos del proceso", variant: "destructive" });
      return;
    }
    setDocuments(result.data || []);
  }, [processLine.id, toast]);

  React.useEffect(() => {
    setDocuments(processLine.documents || []);
  }, [processLine.documents]);

  React.useEffect(() => {
    const checkBucket = async () => {
      const { data, error } = await supabase.storage.from(BUCKET).list("", { limit: 1 });
      setUploadEnabled(!error && Array.isArray(data));
    };
    checkBucket();
  }, []);

  const getDocumentUrl = React.useCallback(
    async (doc, expiresIn = 300, notifyOnError = true) => {
      if (doc.external_url) return doc.external_url;
      if (!doc.storage_path) return "";
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(doc.storage_path, expiresIn);
      if (error) {
        if (notifyOnError) {
          toast({ title: "Error", description: "No se pudo generar el enlace del archivo", variant: "destructive" });
        }
        return "";
      }
      return data.signedUrl;
    },
    [toast]
  );

  React.useEffect(() => {
    let cancelled = false;

    const loadImageUrls = async () => {
      const imageDocs = documents.filter((doc) => isImageDocument(doc) && (doc.external_url || doc.storage_path));
      if (imageDocs.length === 0) {
        setImageUrls({});
        return;
      }
      const entries = await Promise.all(
        imageDocs.map(async (doc) => {
          const url = await getDocumentUrl(doc, 300, false);
          return [doc.id, url];
        })
      );
      if (!cancelled) {
        setImageUrls(Object.fromEntries(entries.filter(([, url]) => Boolean(url))));
      }
    };

    loadImageUrls();
    return () => {
      cancelled = true;
    };
  }, [documents, getDocumentUrl]);

  const reset = () => {
    setDocumentEditorOpen(false);
    setEditingId(null);
    setFormData(initialDocument);
    setSelectedFile(null);
  };

  const uploadFileIfNeeded = async (file = selectedFile) => {
    if (!file) return {};
    const documentId = editingId || crypto.randomUUID();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `process-lines/${processLine.id}/${documentId}/${crypto.randomUUID()}-${safeName}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false });
    if (error) throw error;
    return {
      storage_path: path,
      file_name: file.name,
      mime_type: file.type || null,
      file_size_bytes: file.size,
      external_url: "",
      design_storage_path: null,
      design_version: null,
    };
  };

  const submit = async (event) => {
    event.preventDefault();
    if (readOnly || loading || rotationBusy) return;
    if (!formData.title.trim()) {
      toast({ title: "Validacion", description: "El titulo es obligatorio", variant: "destructive" });
      return;
    }
    if (!selectedFile && !formData.external_url.trim() && !editingId) {
      toast({ title: "Validacion", description: "Adjunta un archivo o indica una URL externa", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      let uploadData = {};
      if (selectedFile) {
        if (!uploadEnabled) {
          toast({
            title: "Storage no configurado",
            description: "Crea el bucket privado mfg-product-docs y sus politicas antes de subir archivos.",
            variant: "destructive",
          });
          return;
        }
        uploadData = await uploadFileIfNeeded();
      }
      const result = await saveProcessDocument(
        { ...formData, ...uploadData, process_line_id: processLine.id },
        editingId
      );
      if (result.error) throw result.error;
      toast({ title: editingId ? "Documento actualizado" : "Documento creado" });
      reset();
      await loadDocuments();
      onChanged?.();
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudo guardar el documento", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const openDesigner = async (doc = null) => {
    if (readOnly || loading) return;
    setLoading(true);
    try {
      const loaded = doc ? await loadProcessDocumentDesign(doc) : {};
      setDesignSession({ document: doc, ...loaded, ...(loaded.design ? { design: { ...loaded.design, ...designContext } } : {}) });
    } catch (error) {
      toast({ title: 'No se pudo abrir el diseño', description: error.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };
  const saveDesign = async ({ design, imageFile, metadata }) => {
    if (readOnly) throw new Error('El escandallo está validado y no se puede modificar.');
    if (!uploadEnabled) throw new Error('El almacenamiento de documentos no está disponible.');
    await saveDesignedProcessDocument({ design, imageFile, metadata: { document_type: 'technical', ...metadata }, document: designSession?.document || {}, processLineId: processLine.id });
    setDesignSession(null);
    await loadDocuments(); onChanged?.();
    toast({ title: 'Ficha guardada', description: 'El diseño se puede editar y el PDF está listo para imprimir.' });
  };

  const edit = (doc) => {
    if (doc.design_storage_path) { openDesigner(doc); return; }
    setDocumentEditorOpen(true);
    setEditingId(doc.id);
    setFormData({
      document_requirement_id: requirements.some((r) => r.id === doc.document_requirement_id) ? doc.document_requirement_id : "",
      document_type: doc.document_type || "other",
      title: doc.title || "",
      storage_path: doc.storage_path || "",
      external_url: doc.external_url || "",
      file_name: doc.file_name || "",
      mime_type: doc.mime_type || "",
      file_size_bytes: doc.file_size_bytes || "",
      notes: doc.notes || "",
      is_default: Boolean(doc.is_default),
    });
    setSelectedFile(null);
  };

  const remove = async (doc) => {
    if (!doc || readOnly || loading || deletingDocument.current) return;
    deletingDocument.current = true;
    setLoading(true);
    try {
      const result = await deleteProcessDocument(doc.id);
      if (result.error) throw result.error;
      setDocumentToDelete(null);
      await loadDocuments();
      onChanged?.();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar el documento", variant: "destructive" });
    } finally {
      deletingDocument.current = false;
      setLoading(false);
    }
  };

  const openDocument = async (doc) => {
    const url = await getDocumentUrl(doc, 60);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const changeDefaultDocument = async (doc, checked) => {
    if (readOnly || loading) return;
    setLoading(true);
    try {
      const result = await saveProcessDocument({ ...doc, process_line_id: processLine.id, is_default: checked }, doc.id);
      if (result.error) throw result.error;
      setDocuments((current) => current.map((item) => ({
        ...item,
        is_default: item.id === doc.id ? checked : checked ? false : item.is_default,
      })));
      if (editingId === doc.id || checked) {
        setFormData((current) => ({ ...current, is_default: editingId === doc.id ? checked : false }));
      }
      await loadDocuments();
      onChanged?.();
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudo cambiar el documento por defecto", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const savePreviewRotation = async (file) => {
    if (readOnly || loading || !previewDoc) return;
    if (!uploadEnabled) throw new Error('No se puede guardar el giro: el almacenamiento no está disponible.');
    setLoading(true);
    try {
      const uploadData = await uploadFileIfNeeded(file);
      const result = await saveProcessDocument({ ...previewDoc, ...uploadData, process_line_id: processLine.id }, previewDoc.id);
      if (result.error) throw result.error;
      const updated = { ...previewDoc, ...uploadData };
      setPreviewDoc(updated);
      setPreviewUrl(await getDocumentUrl(updated, 300));
      await loadDocuments();
      onChanged?.();
      toast({ title: 'Orientación guardada' });
    } finally { setLoading(false); }
  };

  const openPreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewUrl(imageUrls[doc.id] || "");
    setPreviewLoading(true);
    const url = imageUrls[doc.id] || (await getDocumentUrl(doc, 300));
    setPreviewUrl(url);
    setPreviewLoading(false);
  };

  return (
    <div className="rounded border bg-gray-50 p-3">
      <AlertDialog open={Boolean(documentToDelete)} onOpenChange={(open) => { if (!open && !deletingDocument.current) setDocumentToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogTitle>¿Seguro que quieres borrar {documentToDelete && isImageDocument(documentToDelete) ? 'esta imagen' : 'este documento'}?</AlertDialogTitle>
          <AlertDialogDescription>Se eliminará «{documentToDelete?.title || documentToDelete?.file_name}» de los documentos del proceso. Esta acción no se puede deshacer.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={loading} onClick={(event) => { event.preventDefault(); remove(documentToDelete); }}>{loading ? 'Borrando…' : 'Sí, borrar'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="mb-3 flex items-center gap-2 font-medium">
        <FileImage className="h-4 w-4" />
        Documentos e imagenes del proceso
      </div>
      {!readOnly && <div className="flex gap-2">
        <input ref={photoInputRef} className="hidden" type="file" accept="image/*" aria-label="Seleccionar foto del proceso" onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          reset();
          setSelectedFile(file);
          setFormData({ ...initialDocument, title: file.name });
          setDocumentEditorOpen(true);
        }} />
        <Button type="button" disabled={loading} onClick={() => photoInputRef.current?.click()}><FileImage className="mr-2 h-4 w-4" />Añadir foto</Button>
        <Button type="button" variant="outline" disabled={loading} onClick={() => { reset(); setDocumentEditorOpen(true); }}><Plus className="mr-2 h-4 w-4" />Añadir documento</Button>
        <Button type="button" variant="outline" disabled={loading} onClick={() => openDesigner()}><Edit className="mr-2 h-4 w-4" />Diseñar documento</Button>
      </div>}
      <ProcessDocumentDesigner open={Boolean(designSession)} initialDesign={designSession?.design} initialImageFile={designSession?.imageFile} document={designSession?.document} requirements={requirements} context={designContext} onClose={() => setDesignSession(null)} onSave={saveDesign} />
      <Dialog open={documentEditorOpen && !readOnly} onOpenChange={(open) => { if (!open && !loading) reset(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Editar documento' : selectedFile?.type.startsWith('image/') ? 'Añadir foto' : 'Añadir documento'}</DialogTitle>
            <DialogDescription>Descripción y archivo del documento del proceso.</DialogDescription></DialogHeader>
          {!editingId && <Button type="button" variant="outline" disabled={loading || rotationBusy} onClick={() => { reset(); openDesigner(); }}><Edit className="mr-2 h-4 w-4" />Diseñar documento con imagen y comentarios</Button>}
          {selectedFile && <p className="break-all text-sm text-gray-600">Archivo: {selectedFile.name}</p>}
          <fieldset disabled={loading}>
            <form onSubmit={submit} className="grid gap-2 md:grid-cols-6">
              <select
                className="rounded-md border border-input bg-background px-3 py-2"
                value={formData.document_type}
                onChange={(event) => setFormData({ ...formData, document_type: event.target.value })}
              >
                <option value="image">Imagen</option>
                <option value="technical">Ficha tecnica</option>
                <option value="pattern">Patron</option>
                <option value="instruction">Instruccion</option>
                <option value="other">Otro</option>
              </select>
              {requirements.length > 0 && <label className="md:col-span-6 text-sm">Requisito que cubre
                <select className="mt-1 w-full rounded border p-2" value={formData.document_requirement_id}
                  onChange={(event) => setFormData({ ...formData, document_requirement_id: event.target.value })}>
                  <option value="">Documento adicional (no cubre un requisito)</option>
                  {requirements.map((r) => <option key={r.id} value={r.id}>{r.name} · {MFG_COST_SHEET_STAGES.find((stage) => stage.id === r.stage)?.label}</option>)}
                </select>
              </label>}
              <Input className="md:col-span-2" required aria-label="Descripción del documento" placeholder="Descripción" value={formData.title} onChange={(event) => setFormData({ ...formData, title: event.target.value })} />
              <Input className="md:col-span-2" placeholder="URL externa" value={formData.external_url} onChange={(event) => setFormData({ ...formData, external_url: event.target.value })} />
              <Button type="submit" disabled={loading || rotationBusy}>
                {selectedFile ? <Upload className="mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
                {editingId ? "Actualizar" : "Guardar"}
              </Button>
              <Input className="md:col-span-3" placeholder="Notas" value={formData.notes} onChange={(event) => setFormData({ ...formData, notes: event.target.value })} />
              <label className="flex items-center gap-2 text-sm md:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(formData.is_default)}
                  onChange={(event) => setFormData({ ...formData, is_default: event.target.checked })}
                />
                Doc. por defecto
              </label>
              <Input className="md:col-span-2" type="file" onChange={(event) => {
                const file = event.target.files?.[0] || null;
                setSelectedFile(file);
                if (file) setFormData((current) => ({ ...current, title: file.name, external_url: '', document_type: file.type.startsWith('image/') ? 'image' : current.document_type }));
              }} />
              <Button type="button" variant="ghost" onClick={reset}>
                Cancelar
              </Button>
            </form>
          </fieldset>
          {selectedFile && documentPreviewKind(selectedFile) && <RotatableDocumentPreview file={selectedFile} autoApply onApply={setSelectedFile} onBusyChange={setRotationBusy} disabled={loading} />}
        </DialogContent>
      </Dialog>
      <div className="mt-3 space-y-2">
        {documents.map((doc) => (
          <div key={doc.id} className={`flex flex-col justify-between gap-2 rounded border p-2 transition-colors md:flex-row md:items-center ${doc.is_default ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white"}`}>
            <div className="flex gap-3">
              {isImageDocument(doc) && (
                <button
                  type="button"
                  className="flex h-16 w-20 shrink-0 items-center justify-center overflow-hidden rounded border bg-gray-100 text-gray-500"
                  onClick={() => openPreview(doc)}
                  aria-label={`Previsualizar ${doc.title}`}
                >
                  {imageUrls[doc.id] ? (
                    <img src={imageUrls[doc.id]} alt={doc.title} className="h-full w-full object-cover" />
                  ) : (
                    <FileImage className="h-5 w-5" />
                  )}
                </button>
              )}
              <div>
                <div className="font-medium">{doc.title}{doc.is_default ? " · Por defecto" : ""}{doc.design_storage_path ? " · Ficha editable" : ""}</div>
                {requirements.length > 0 && <div className="text-xs text-blue-700">{requirements.find((r) => r.id === doc.document_requirement_id)?.name || 'Documento adicional'}</div>}
                <div className="text-sm text-gray-600">{doc.file_name || doc.external_url || doc.document_type}</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 whitespace-nowrap text-sm">
                <input
                  type="checkbox"
                  aria-label={`Documento por defecto: ${doc.title}`}
                  checked={Boolean(doc.is_default)}
                  disabled={readOnly || loading}
                  onChange={(event) => changeDefaultDocument(doc, event.target.checked)}
                />
                Por defecto
              </label>
              {documentPreviewKind(doc) && (
                <Button variant="outline" size="sm" onClick={() => openPreview(doc)}>
                  <FileImage className="mr-1 h-4 w-4" />
                  Ver
                </Button>
              )}
              {(doc.external_url || doc.storage_path) && (
                <Button variant="outline" size="sm" onClick={() => openDocument(doc)}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
              {!readOnly && (
                <>
                  <Button variant="outline" size="sm" aria-label={`Editar documento ${doc.title}`} disabled={loading} onClick={() => edit(doc)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="destructive" size="sm" disabled={loading} aria-label={`Borrar ${isImageDocument(doc) ? 'imagen' : 'documento'} ${doc.title}`} onClick={() => setDocumentToDelete(doc)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
        {documents.length === 0 && <p className="text-sm text-gray-500">Sin documentos en este proceso.</p>}
      </div>
      <Dialog open={Boolean(previewDoc)} onOpenChange={(open) => { if (!open && !loading) { setPreviewDoc(null); setPreviewUrl(""); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewDoc?.title || "Previsualización"}</DialogTitle>
            <DialogDescription>{previewDoc?.file_name || previewDoc?.external_url || "Imagen vinculada al proceso"}</DialogDescription>
          </DialogHeader>
          {previewDoc?.design_storage_path && !readOnly && <p className="text-sm text-gray-600">Para cambiar la orientación de la ficha guardada, abre Editar documento y ajusta el formato o gira la imagen en el editor.</p>}
          <div className="min-h-72">
            {previewLoading ? (
              <p className="text-sm text-gray-500">Cargando previsualización...</p>
            ) : previewUrl ? (
              <RotatableDocumentPreview key={previewDoc.id} url={previewUrl} document={previewDoc} onApply={readOnly || previewDoc.design_storage_path ? undefined : savePreviewRotation} disabled={loading} />
            ) : (
              <p className="text-sm text-gray-500">No se pudo cargar la previsualización.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotesDialog({ data, onClose, onSaved, readOnly = false }) {
  const [value, setValue] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const { toast } = useToast();

  React.useEffect(() => {
    setValue(data?.line?.notes || "");
  }, [data]);

  const isProcess = data?.type === "process";
  const line = data?.line;

  const submit = async () => {
    if (!line) return;
    setSaving(true);
    try {
      const result = isProcess
        ? await saveProcessLine(
            {
              cost_sheet_id: line.cost_sheet_id,
              process_id: line.process_id,
              supplier_id: line.supplier_id,
              quantity: line.quantity,
              unit_cost: line.unit_cost,
              sort_order: line.sort_order,
              notes: value,
            },
            line.id
          )
        : await saveMaterialLine(
            {
              cost_sheet_id: line.cost_sheet_id,
              process_line_id: line.process_line_id,
              material_id: line.material_id,
              material_supplier_id: line.material_supplier_id,
              quantity: line.quantity,
              unit: line.unit,
              waste_pct: line.waste_pct,
              unit_cost: line.unit_cost,
              sort_order: line.sort_order,
              notes: value,
            },
            line.id
          );
      if (result.error) throw result.error;
      toast({ title: "Observaciones guardadas" });
      await onSaved?.();
      onClose();
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudieron guardar las observaciones", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const title = isProcess
    ? `Observaciones · ${line?.process?.description || "Proceso"}`
    : `Observaciones · ${line?.material?.internal_description || "Material"}`;

  return (
    <Dialog open={Boolean(data)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {readOnly ? "Consulta las observaciones de esta línea." : "Consulta y edita las observaciones de esta línea."}
          </DialogDescription>
        </DialogHeader>
        <textarea
          className="min-h-40 w-full rounded-md border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:bg-gray-50"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="Escribe aquí las observaciones..."
          readOnly={readOnly}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {readOnly ? "Cerrar" : "Cancelar"}
          </Button>
          {!readOnly && (
            <Button type="button" onClick={submit} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              Guardar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StageMarks({ sheet, onChange, locked = false }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-col justify-between gap-1 sm:flex-row sm:items-center">
        <h3 className="font-semibold">Estado del escandallo</h3>
        <span className="text-xs text-gray-500">
          {locked
            ? "Pulsa Validación e introduce la contraseña para desbloquear la ficha"
            : "Los estados se completan al superar sus checks. Validación es independiente."}
        </span>
      </div>
      {locked && (
        <div className="mb-3 flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>El escandallo está validado. La ficha del artículo no se puede modificar.</span>
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        {MFG_COST_SHEET_STAGES.map((stage) => {
          const value = sheet?.[stage.column] ?? null;
          const meta = stageStateMeta(value);
          const isValidation = stage.id === "validation";
          return (
            <button
              key={stage.id}
              type="button"
              disabled={!sheet || !isValidation}
              onClick={() => onChange(stage, nextStageValue(stage, value))}
              className={`flex min-w-40 flex-col gap-1 rounded-md border-2 px-4 py-3 text-left shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60 ${meta.className}`}
            >
              <span className="flex items-center gap-1.5 text-sm font-semibold">
                {isValidation && locked ? <Lock className="h-3.5 w-3.5" /> : null}
                {stage.label}
              </span>
              <span className="text-xs font-medium uppercase tracking-wide">{meta.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ValidationPasswordDialog({ open, nextLabel, onCancel, onConfirm }) {
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setPassword("");
      setError("");
    }
  }, [open]);

  const submit = (event) => {
    event.preventDefault();
    if (password !== MFG_COST_SHEET_VALIDATION_PASSWORD) {
      setError("Contraseña incorrecta");
      return;
    }
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent className="z-[80] max-w-md">
        <DialogHeader>
          <DialogTitle>Confirmar cambio de validación</DialogTitle>
          <DialogDescription>
            Introduce la contraseña para cambiar el estado a {nextLabel}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-gray-700">Contraseña</span>
            <Input
              type="password"
              autoFocus
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setError("");
              }}
              placeholder="Contraseña de validación"
            />
          </label>
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button type="submit">Confirmar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CostSheetManager({
  productId: controlledProductId = null,
  hideProductSelector = false,
  readOnly = false,
  reloadKey = 0,
  onValidationChange,
  onTotalsChange,
  onAutomaticPrices,
  beforeCloseRef,
  onBeforeValidate,
}) {
  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [internalProductId, setInternalProductId] = React.useState(null);
  const productId = controlledProductId || internalProductId;
  const [sourceProduct, setSourceProduct] = React.useState(null);
  const [copying, setCopying] = React.useState(false);
  const copyingRef = React.useRef(false);
  const [templateOpen, setTemplateOpen] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [selectedSheet, setSelectedSheet] = React.useState(null);
  const [materials, setMaterials] = React.useState([]);
  const [suppliers, setSuppliers] = React.useState([]);
  const [processes, setProcesses] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState("");
  const [activeProcessId, setActiveProcessId] = React.useState("");
  const [materialPreview, setMaterialPreview] = React.useState(null);
  const pricingSnapshot = React.useRef(null);
  const [colorPresetsOpen, setColorPresetsOpen] = React.useState(false);
  const [materialDialogOpen, setMaterialDialogOpen] = React.useState(false);
  const [materialEditingLine, setMaterialEditingLine] = React.useState(null);
  const [processLineEditingId, setProcessLineEditingId] = React.useState(null);
  const [processLine, setProcessLine] = React.useState(initialProcessLine);
  const [quickActionsOpen, setQuickActionsOpen] = React.useState(false);
  const [notesDialog, setNotesDialog] = React.useState(null);
  const [docsDialog, setDocsDialog] = React.useState(null);
  const [pendingValidation, setPendingValidation] = React.useState(null);
  const { toast } = useToast();
  const sheetLocked = readOnly || isCostSheetValidated(selectedSheet);

  const loadCatalogs = React.useCallback(async () => {
    const [materialResult, supplierResult, processResult, templateResult] = await Promise.all([
      fetchMaterials(),
      fetchSuppliers("", { activeOnly: true }),
      fetchProcesses({ activeOnly: true }),
      fetchTemplates(),
    ]);
    setMaterials(materialResult.data || []);
    setSuppliers(supplierResult.data || []);
    setProcesses(processResult.data || []);
    setTemplates((templateResult.data || []).filter((template) => template.is_active !== false));
  }, []);

  const loadSheets = React.useCallback(async () => {
    if (!productId) {
      setSelectedSheet((current) => (current === null ? current : null));
      return;
    }
    const result = readOnly ? await fetchCostSheets(productId) : await fetchOrCreateCostSheet(productId);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo cargar el escandallo", variant: "destructive" });
    }
    setSelectedSheet((readOnly ? result.data?.[0] : result.data) || null);
  }, [productId, reloadKey, readOnly, toast]);

  const loadProduct = React.useCallback(async () => {
    if (!productId) {
      setSelectedProduct(null);
      return;
    }
    const result = await fetchManufacturingProduct(productId);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo cargar el articulo de fabricacion", variant: "destructive" });
      return;
    }
    setSelectedProduct(result.data || null);
  }, [productId, toast]);

  React.useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  React.useEffect(() => {
    loadSheets();
  }, [loadSheets]);

  React.useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  React.useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    const refresh = async () => {
      if (document.visibilityState === "hidden") return;
      const [sheets, product] = await Promise.all([fetchCostSheets(productId), fetchManufacturingProduct(productId)]);
      if (cancelled) return;
      if (!sheets.error && sheets.data?.[0]) setSelectedSheet(sheets.data[0]);
      if (!product.error && product.data) setSelectedProduct(product.data);
    };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => { cancelled = true; window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [productId]);

  React.useEffect(() => {
    const familyCode = selectedProduct?.cod_fam?.trim();
    if (!familyCode) return;
    const match = templates.find((template) => String(template.cod_fam || "").trim() === familyCode);
    if (match) setSelectedTemplateId(match.id);
  }, [selectedProduct, templates]);

  React.useEffect(() => {
    if (!selectedSheet) return;
    onValidationChange?.(isCostSheetValidated(selectedSheet));
  }, [selectedSheet, onValidationChange]);

  React.useEffect(() => {
    if (!selectedSheet) return;
    const previewSheet = sheetLocked ? selectedSheet : liveCostSheet(selectedSheet, processLine, processLineEditingId,
      materialDialogOpen ? materialPreview : null, materialEditingLine?.id);
    const total = calculateSheetTotals(previewSheet).total;
    onTotalsChange?.(total);
    const signature = JSON.stringify([selectedSheet.process_lines, selectedSheet.material_lines, total]);
    const previous = pricingSnapshot.current;
    pricingSnapshot.current = { id: selectedSheet.id, signature };
    if (!sheetLocked && previous?.id === selectedSheet.id && previous.signature !== signature) onAutomaticPrices?.(total);
  }, [selectedSheet, sheetLocked, processLine, processLineEditingId, materialDialogOpen, materialPreview, materialEditingLine, onTotalsChange, onAutomaticPrices]);

  const changeStage = async (stage, nextValue) => {
    if (!selectedSheet) return;
    const result = await saveCostSheet(
      { ...selectedSheet, product_id: selectedSheet.product_id, [stage.column]: nextValue },
      selectedSheet.id
    );
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo actualizar la marca", variant: "destructive" });
      return;
    }
    await loadSheets();
  };

  const requestStageChange = (stage, nextValue) => {
    if (readOnly) return;
    if (stage.id === "validation") {
      setPendingValidation({ stage, nextValue });
      return;
    }
    changeStage(stage, nextValue);
  };

  const confirmValidationChange = async () => {
    if (!pendingValidation) return;
    const { stage, nextValue } = pendingValidation;
    if (nextValue === "validated" && onBeforeValidate && !(await onBeforeValidate())) return;
    setPendingValidation(null);
    await changeStage(stage, nextValue);
  };

  const openNewMaterialLine = () => {
    if (sheetLocked || !activeProcess) return;
    setMaterialEditingLine(null);
    setMaterialDialogOpen(true);
  };

  const openEditMaterialLine = (materialLineRow) => {
    if (sheetLocked || !materialLineRow?.process_line_id) return;
    setActiveProcessId(materialLineRow.process_line_id);
    setMaterialEditingLine(materialLineRow);
    setMaterialDialogOpen(true);
  };

  const handleMaterialDialogOpenChange = (open) => {
    setMaterialDialogOpen(open);
    if (!open) setMaterialEditingLine(null);
  };

  const persistMaterialLine = async (payload) => {
    if (sheetLocked) {
      return { error: { message: "El escandallo está validado y no se puede modificar." } };
    }
    if (!selectedSheet || !activeProcess) {
      return { error: { message: "Selecciona un proceso" } };
    }
    const result = await saveMaterialLine(
      {
        ...payload,
        process_line_id: activeProcess.id,
        cost_sheet_id: selectedSheet.id,
      },
      materialEditingLine?.id || null
    );
    if (!result.error) {
      await loadCatalogs();
      await loadSheets();
    }
    return result;
  };

  const submitProcessLine = async (event) => {
    event.preventDefault();
    if (sheetLocked || !selectedSheet || !processLine.process_id) return true;
    const nextSortOrder = processLineEditingId ? processLine.sort_order : processLine.sort_order || processLines.length + 1;
    const result = await saveProcessLine(
      { ...processLine, cost_sheet_id: selectedSheet.id, supplier_id: processLine.supplier_id || null, quantity: processLine.quantity ?? 1, sort_order: nextSortOrder },
      processLineEditingId
    );
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar la línea", variant: "destructive" });
      return false;
    }
    setProcessLine(initialProcessLine);
    setProcessLineEditingId(null);
    if (result.data?.id) setActiveProcessId(result.data.id);
    await loadSheets();
    return true;
  };

  React.useEffect(() => {
    if (!beforeCloseRef) return;
    beforeCloseRef.current = () => submitProcessLine({ preventDefault() {} });
    return () => { beforeCloseRef.current = null; };
  });

  const applyTemplate = async () => {
    if (sheetLocked || !selectedSheet || !selectedTemplateId) return;
    const template = templates.find((item) => item.id === selectedTemplateId);
    if (!template) return;
    const hasLines = (selectedSheet.material_lines?.length || 0) + (selectedSheet.process_lines?.length || 0) > 0;
    if (
      hasLines &&
      !window.confirm(
        "Al aplicar la plantilla se eliminarán todos los procesos, materiales y documentos vinculados a procesos del escandallo actual. ¿Continuar?"
      )
    ) {
      return;
    }

    const result = await applyTemplateToCostSheet(selectedSheet.id, selectedTemplateId);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo aplicar la plantilla", variant: "destructive" });
      return;
    }
    const skippedMaterials = Number(result.data?.skipped_material_count || 0);
    toast({
      title: "Plantilla aplicada",
      description: skippedMaterials
        ? `${result.data?.process_count || 0} procesos y ${result.data?.material_count || 0} materiales importados. ${skippedMaterials} materiales sin proceso no se importaron.`
        : `${result.data?.process_count || 0} procesos y ${result.data?.material_count || 0} materiales importados.`,
    });
    setTemplateOpen(false);
    setSelectedTemplateId("");
    setActiveProcessId("");
    await loadSheets();
  };

  const copyFromProduct = async () => {
    if (sheetLocked || !selectedSheet || !sourceProduct || copyingRef.current) return;
    if (!window.confirm(`¿Sobrescribir todo el escandallo de ${selectedProduct?.code} con el de ${sourceProduct.code} · ${sourceProduct.description}? Se sustituirán los procesos, materiales y documentos de los procesos.`)) return;
    copyingRef.current = true; setCopying(true);
    try {
      const { error } = await supabase.rpc('mfg_replace_cost_sheet_from_product', { p_target: selectedSheet.id, p_source: sourceProduct.id });
      if (error) throw error;
      setProcessLineEditingId(null); setProcessLine(initialProcessLine); setActiveProcessId('');
      setQuickActionsOpen(false); setSourceProduct(null);
      await loadSheets();
      toast({ title: 'Escandallo sustituido' });
    } catch (error) {
      toast({ title: 'No se pudo sustituir el escandallo', description: error.message, variant: 'destructive' });
    } finally { copyingRef.current = false; setCopying(false); }
  };

  const reorderProcesses = async (reordered) => {
    if (sheetLocked || !selectedSheet) return;
    const nextLines = reordered.map((line, index) => ({ ...line, sort_order: index + 1 }));
    setSelectedSheet((current) => (current ? { ...current, process_lines: nextLines } : current));
    if (processLineEditingId) {
      const edited = nextLines.find((line) => line.id === processLineEditingId);
      if (edited) setProcessLine((current) => ({ ...current, sort_order: edited.sort_order }));
    }
    const result = await reorderCostSheetProcessLines(reordered);
    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo guardar el orden de los procesos",
        variant: "destructive",
      });
      await loadSheets();
    }
  };

  const reorderMaterials = async (reordered) => {
    if (sheetLocked || !selectedSheet || !activeProcess) return;
    const nextLines = reordered.map((line, index) => ({ ...line, sort_order: index + 1 }));
    const orderById = new Map(nextLines.map((line) => [line.id, line.sort_order]));
    setSelectedSheet((current) => {
      if (!current) return current;
      return {
        ...current,
        process_lines: (current.process_lines || []).map((process) =>
          process.id === activeProcess.id ? { ...process, material_lines: nextLines } : process
        ),
        material_lines: (current.material_lines || []).map((line) =>
          orderById.has(line.id) ? { ...line, sort_order: orderById.get(line.id) } : line
        ),
      };
    });
    if (materialEditingLine) {
      const edited = nextLines.find((line) => line.id === materialEditingLine.id);
      if (edited) setMaterialEditingLine(edited);
    }
    const result = await reorderCostSheetMaterialLines(reordered);
    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo guardar el orden de los materiales",
        variant: "destructive",
      });
      await loadSheets();
    }
  };

  const processLines = [...(selectedSheet?.process_lines || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const activeProcess = processLines.find((line) => line.id === activeProcessId) || processLines[0] || null;
  const sheetTotals = selectedSheet?.totals || calculateSheetTotals(selectedSheet || {});

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
          <div>
            <h2 className="text-xl font-semibold">Escandallo del articulo</h2>
            <p className="text-sm text-gray-600">
              {selectedProduct ? `${selectedProduct.code} - ${selectedProduct.description}` : "Define procesos, materiales y tarifas del articulo."}
            </p>
            {selectedProduct?.family?.descrip && (
              <p className="text-xs text-gray-500">
                Familia {selectedProduct.cod_fam?.trim()} · {selectedProduct.family.descrip}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 rounded border bg-gray-50 px-4 py-2">
            <span className="text-sm font-medium text-gray-600">Total coste</span>
            <span className="text-2xl font-bold text-primary">{formatMoney(sheetTotals.total)}</span>
          </div>
        </div>
        {hideProductSelector ? (
          null
        ) : (
          <div className="mt-3">
            <ManufacturingProductSelector value={productId} onSelect={setInternalProductId} allowPublish />
          </div>
        )}
      </div>

      {productId && (
        <div className="space-y-4">
          {!hideProductSelector && selectedProduct && (
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <h3 className="mb-3 font-semibold">Precios de Venta de Tarifas</h3>
              {selectedProduct.family?.descrip && (
                <p className="mb-3 text-sm text-gray-600">
                  Familia {selectedProduct.cod_fam?.trim()} · {selectedProduct.family.descrip}
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                {LEGACY_SALE_PRICE_FIELDS.map((field, index) => (
                  <label key={field} className="space-y-1 text-sm">
                    <span className="font-medium text-gray-700">{salePriceLabels[index]}</span>
                    <Input readOnly value={Number(selectedProduct[field] || 0).toFixed(2)} className="bg-gray-50" />
                  </label>
                ))}
              </div>
            </div>
          )}

          <StageMarks sheet={selectedSheet} onChange={requestStageChange} locked={sheetLocked} />
          {selectedSheet && <CostSheetWorkflow sheet={selectedSheet} product={selectedProduct} locked={sheetLocked} onSaved={loadSheets} onEditMaterial={(lineId) => openEditMaterialLine(selectedSheet.material_lines?.find((line) => line.id === lineId))} />}

          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={() => { setSourceProduct(null); setQuickActionsOpen(true); }} disabled={!selectedSheet || sheetLocked}>
              Acciones rápidas
            </Button>
          </div>

          <div className="space-y-4">
            {selectedSheet ? (
              <>
                <div className="rounded-lg border bg-white shadow-sm">
                  <h3 className="border-b bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900">Procesos y costes</h3>
                  <div className="space-y-3 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={async () => { try { setPdfUrl(await printCostSheetCompact({ product: selectedProduct, sheet: selectedSheet })); } catch (error) { toast({ title: "No se pudo imprimir", description: error.message, variant: "destructive" }); } }}>
                          <Printer className="mr-2 h-4 w-4" />
                          Imprimir
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => setTemplateOpen(true)} disabled={sheetLocked}>
                          Importar Plantilla
                        </Button>
                        <Button type="button" variant="outline" size="sm"
                          className={selectedSheet.color_preset?.variants?.length > 0
                            ? "border-green-600 bg-green-600 text-white hover:border-green-700 hover:bg-green-700 hover:text-white"
                            : undefined}
                          onClick={() => setColorPresetsOpen(true)}>Variantes de color</Button>
                      </div>
                      <div className="text-xs text-gray-600">
                        Procesos {formatMoney(sheetTotals.processOwnTotal)} · Materiales {formatMoney(sheetTotals.materialTotal)} ·{" "}
                        <span className="font-bold text-gray-900">Total {formatMoney(sheetTotals.total)}</span>
                      </div>
                    </div>

                    <CostSheetProcessMaterialsView
                      sheet={selectedSheet}
                      processLines={processLines}
                      activeProcessId={activeProcess?.id}
                      onActiveProcessChange={setActiveProcessId}
                      onReorderProcesses={sheetLocked ? undefined : reorderProcesses}
                      onReorderMaterials={sheetLocked ? undefined : reorderMaterials}
                      sheetTotals={sheetTotals}
                      materialHeaderAction={sheetLocked ? null : (
                        <Button type="button" size="sm" onClick={openNewMaterialLine} disabled={!activeProcess}>
                          <Plus className="mr-2 h-4 w-4" />
                          Añadir material
                        </Button>
                      )}
                      processFooter={sheetLocked ? null : (
                        <form onSubmit={submitProcessLine} className="grid gap-2 border-t p-3 sm:grid-cols-12">
                          <select
                            className="rounded-md border border-input bg-background px-2 py-2 text-sm sm:col-span-7"
                            value={processLine.process_id}
                            onChange={(event) => setProcessLine({ ...processLine, process_id: event.target.value })}
                          >
                            <option value="">Proceso</option>
                            {processes.map((process) => (
                              <option key={process.id} value={process.id}>
                                {process.description}
                              </option>
                            ))}
                          </select>
                          <Input className="sm:col-span-3" type="number" min="0" step="0.0001" value={processLine.unit_cost} onChange={(event) => setProcessLine({ ...processLine, unit_cost: event.target.value })} placeholder="Precio" />
                          <Button className="sm:col-span-2" type="submit" size="sm" title={processLineEditingId ? "Actualizar" : "Añadir"}>
                            {processLineEditingId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                          </Button>
                          {processLineEditingId && (
                            <Button className="sm:col-span-12" type="button" variant="ghost" size="sm" onClick={() => { setProcessLineEditingId(null); setProcessLine(initialProcessLine); }}>
                              <X className="mr-1 h-4 w-4" />
                              Cancelar edición
                            </Button>
                          )}
                        </form>
                      )}
                      renderProcessActions={(line) => (
                        <>
                          <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" title="Observaciones del proceso" onClick={() => setNotesDialog({ type: "process", line })}>
                            <BookOpen className={`h-4 w-4 ${line.notes ? "text-primary" : "text-gray-400"}`} />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" className="relative h-8 w-8 p-0" title="Documentos e imágenes" onClick={() => { setActiveProcessId(line.id); setDocsDialog(line); }}>
                            <FileImage className={`h-4 w-4 ${line.documents?.length ? "text-primary" : "text-gray-400"}`} />
                            {line.documents?.length ? (
                              <span className="absolute -right-1 -top-1 rounded-full bg-primary px-1 text-[10px] font-bold leading-tight text-primary-foreground">{line.documents.length}</span>
                            ) : null}
                          </Button>
                          {!sheetLocked && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Editar proceso"
                                onClick={() => {
                                  setActiveProcessId(line.id);
                                  setProcessLineEditingId(line.id);
                                  setProcessLine({
                                    process_id: line.process_id,
                                    supplier_id: "",
                                    quantity: 1,
                                    unit_cost: line.unit_cost,
                                    sort_order: line.sort_order,
                                    notes: line.notes || "",
                                  });
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="destructive" size="sm" className="h-8 w-8 p-0" title="Eliminar proceso" onClick={async () => { await deleteProcessLine(line.id); await loadSheets(); }}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </>
                      )}
                      renderMaterialActions={(materialLineRow) => (
                        <>
                          <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" title="Observaciones de la línea" onClick={() => setNotesDialog({ type: "material", line: materialLineRow })}>
                            <BookOpen className={`h-4 w-4 ${String(materialLineRow.notes || "").trim() ? "text-green-600" : "text-gray-400"}`} />
                          </Button>
                          {!sheetLocked && (
                            <>
                              <Button variant="outline" size="sm" className="h-8 w-8 p-0" title="Editar material" onClick={() => openEditMaterialLine(materialLineRow)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="destructive" size="sm" className="h-8 w-8 p-0" title="Eliminar material" onClick={async () => { await deleteMaterialLine(materialLineRow.id); await loadSheets(); }}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>
                {(selectedSheet.orphan_material_lines || []).length > 0 && (
                  <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900 shadow-sm">
                    <h3 className="mb-2 font-semibold">Materiales sin proceso</h3>
                    <p className="mb-3">Estos materiales vienen del modelo plano anterior. Selecciona un proceso y reasignalos.</p>
                    <div className="space-y-2">
                      {selectedSheet.orphan_material_lines.map((line) => (
                        <div key={line.id} className="flex flex-col justify-between gap-2 rounded border bg-white p-2 md:flex-row md:items-center">
                          <span>{line.material?.internal_description || "Material"} · {formatMoney(line.line_cost)}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!activeProcess || sheetLocked}
                            onClick={async () => {
                              await saveMaterialLine({ ...line, process_line_id: activeProcess.id }, line.id);
                              await loadSheets();
                            }}
                          >
                            Asignar a {activeProcess?.process?.description || "proceso seleccionado"}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg border bg-white p-6 text-sm text-gray-600 shadow-sm">
                Cargando el escandallo del artículo...
              </div>
            )}
          </div>
        </div>
      )}

      <CostSheetColorPresetDialog sheet={selectedSheet} open={colorPresetsOpen} onOpenChange={setColorPresetsOpen}
        readOnly={sheetLocked} onSaved={loadSheets} />
      <CostSheetMaterialLineDialog
        open={materialDialogOpen}
        onOpenChange={handleMaterialDialogOpenChange}
        processDescription={activeProcess?.process?.description || ""}
        hasProcess={Boolean(activeProcess)}
        editingLine={materialEditingLine}
        currentMaterialCount={activeProcess?.material_lines?.length || 0}
        materials={materials}
        suppliers={suppliers}
        onMaterialsChange={setMaterials}
        onSave={persistMaterialLine}
        onDefineColorVariants={() => setColorPresetsOpen(true)}
        onPreviewChange={setMaterialPreview}
      />

      <Dialog open={quickActionsOpen} onOpenChange={(open) => { if (!copying) setQuickActionsOpen(open); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Copiar escandallo de otro artículo</DialogTitle>
            <DialogDescription>Selecciona un artículo de la misma familia. Al aplicar, se sustituirá todo el escandallo actual.</DialogDescription></DialogHeader>
          <CostSheetSourcePicker family={selectedProduct?.cod_fam?.trim()} excludeId={productId} value={sourceProduct} onSelect={setSourceProduct} disabled={copying} />
          <DialogFooter><Button type="button" variant="outline" disabled={copying} onClick={() => setQuickActionsOpen(false)}>Cancelar</Button>
            <Button type="button" onClick={copyFromProduct} disabled={!sourceProduct || !selectedSheet || sheetLocked || copying}><Copy className="mr-2 h-4 w-4" />{copying ? 'Aplicando…' : 'Aplicar'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent><DialogHeader><DialogTitle>Importar plantilla</DialogTitle><DialogDescription>Sustituye el escandallo actual por la plantilla seleccionada.</DialogDescription></DialogHeader>
          <select className="w-full rounded-md border p-2" value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}>
            <option value="">Plantilla</option>{templates.map((template) => <option key={template.id} value={template.id}>{formatFamilyTemplateOption(template)}</option>)}
          </select>
          <Button type="button" onClick={applyTemplate} disabled={!selectedTemplateId || !selectedSheet || sheetLocked}>Aplicar</Button>
        </DialogContent>
      </Dialog>

      <PdfPreview url={pdfUrl} onClose={() => setPdfUrl(null)} title="Escandallo compacto" filename={`escandallo-${String(selectedProduct?.code || 'articulo').replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-').trim()}.pdf`} />
      <NotesDialog data={notesDialog} onClose={() => setNotesDialog(null)} onSaved={loadSheets} readOnly={sheetLocked} />

      <Dialog open={Boolean(docsDialog)} onOpenChange={(open) => { if (!open) setDocsDialog(null); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Documentos e imágenes · {docsDialog?.process?.description || "Proceso"}</DialogTitle>
            <DialogDescription>
              {sheetLocked
                ? "Consulta los documentos vinculados al proceso."
                : "Adjunta, edita y previsualiza los documentos vinculados al proceso."}
            </DialogDescription>
          </DialogHeader>
          {docsDialog && <ProcessDocuments product={selectedProduct} processLine={selectedSheet?.process_lines?.find((line) => line.id === docsDialog.id) || docsDialog} onChanged={loadSheets} readOnly={sheetLocked} />}
        </DialogContent>
      </Dialog>

      <ValidationPasswordDialog
        open={Boolean(pendingValidation)}
        nextLabel={stageStateMeta(pendingValidation?.nextValue).label}
        onCancel={() => setPendingValidation(null)}
        onConfirm={confirmValidationChange}
      />
    </div>
  );
}
